import type { BusinessSubtype, BusinessType } from '../utils/store-lexicon';

export type BxAppId =
  | 'marketplace'
  | 'runner'
  | 'merchant'
  | 'xchange'
  | 'xbuy'
  | 'xstays'
  | 'xcourier'
  | 'xproxy'
  | 'xevents';

export type GuidedStepGate = 'wallet' | 'persona_merchant' | 'persona_runner';

export interface GuidedStepField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'select';
  placeholder?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
}

export interface GuidedStep {
  id: string;
  title: string;
  description: string;
  gate?: GuidedStepGate;
  fields?: GuidedStepField[];
}

export interface BxAppDefinition {
  id: BxAppId;
  name: string;
  shortName: string;
  tagline: string;
  description: string;
  /** Lucide icon key used by AppsLauncher */
  icon: string;
  accent: string;
  /** Primary deep-link when flow completes */
  launchHref: string;
  /** Standalone guided hub */
  hubPath: string;
  businessType?: BusinessType;
  businessSubtype?: BusinessSubtype;
  /** Hide from launcher when persona lacks activation */
  requiresPersona?: 'merchant' | 'runner';
  paymentOptions?: BxAppId[];
  deliveryOptions?: BxAppId[];
  steps: GuidedStep[];
  launchLabel: string;
}

const walletGateStep = (nextTitle: string): GuidedStep => ({
  id: 'connect-wallet',
  title: 'Connect your wallet',
  description:
    'Non-custodial escrow requires a Web3 wallet. Connect once — your session persists across all BX apps.',
  gate: 'wallet',
});

export const BX_APPS: BxAppDefinition[] = [
  {
    id: 'marketplace',
    name: 'Marketplace',
    shortName: 'Market',
    tagline: 'Browse everything in one place',
    description:
      'Browse listings, Town Hall, and start conversations — settlement happens on external rails when you are ready.',
    icon: 'Store',
    accent: '#01BC8D',
    launchHref: '/marketplace',
    hubPath: '/apps/marketplace',
    steps: [
      {
        id: 'browse',
        title: 'Browse listings',
        description: 'Explore categories from collectibles to services across Jamaica.',
      },
      {
        id: 'inquire',
        title: 'Start a conversation',
        description: 'Message the seller or post to Town Hall when you need a match.',
      },
      {
        id: 'escrow',
        title: 'Fund UTT escrow',
        description: 'Upgrade to non-custodial escrow when both parties are ready.',
        gate: 'wallet',
      },
      {
        id: 'track',
        title: 'Track your trade',
        description: 'Follow PIN handoff and settlement in your Activity order book.',
      },
    ],
    launchLabel: 'Open Marketplace',
  },
  {
    id: 'runner',
    name: 'Runner App',
    shortName: 'Runner',
    tagline: 'Claim gigs and settle with PIN',
    description: 'Field operations for proxy runners — noticeboard, claim, handoff, and Web3 payout.',
    icon: 'Truck',
    accent: '#38bdf8',
    launchHref: '/runner',
    hubPath: '/apps/runner',
    requiresPersona: 'runner',
    businessType: 'delegated_supply_chain',
    businessSubtype: 'field_service_dispatch',
    steps: [
      walletGateStep('Browse noticeboard'),
      {
        id: 'noticeboard',
        title: 'Browse funded gigs',
        description: 'Live noticeboard shows escrow-funded proxy and courier jobs near you.',
      },
      {
        id: 'claim',
        title: 'Claim a gig',
        description: 'One runner per gig — claim locks the job to your wallet.',
      },
      {
        id: 'execute',
        title: 'Execute & confirm PIN',
        description: 'Complete pickup/delivery checklist and enter the Handshake PIN to release escrow.',
      },
    ],
    launchLabel: 'Open Runner Cockpit',
  },
  {
    id: 'merchant',
    name: 'Merchant App',
    shortName: 'Merchant',
    tagline: 'Stores, storefronts, and order books',
    description: 'Create vertical stores, customize Puck storefronts, and manage logistics or rental calendars.',
    icon: 'Package',
    accent: '#a78bfa',
    launchHref: '/merchant',
    hubPath: '/apps/merchant',
    requiresPersona: 'merchant',
    steps: [
      {
        id: 'persona',
        title: 'Activate merchant persona',
        description: 'Switch to Merchant in your profile or connect a delegated merchant wallet.',
        gate: 'persona_merchant',
      },
      {
        id: 'store',
        title: 'Create your store',
        description: 'Pick settlement model and vertical — 5 categories, 20+ subtypes.',
      },
      {
        id: 'storefront',
        title: 'Publish storefront',
        description: 'Customize layout with Puck and share your /store/:slug link.',
      },
      {
        id: 'orders',
        title: 'Manage order book',
        description: 'Dispatch board or rental calendar — fund gigs and track settlement.',
      },
    ],
    launchLabel: 'Open Merchant Hub',
  },
  {
    id: 'xchange',
    name: 'xChange',
    shortName: 'xChange',
    tagline: 'Swap digital assets for cash',
    description: 'P2P crypto-to-fiat proxy arbitrage — lock USDC in UTT, runner delivers JMD premium.',
    icon: 'ArrowLeftRight',
    accent: '#22c55e',
    launchHref: '/marketplace?intent=xchange',
    hubPath: '/apps/xchange',
    businessType: 'p2p_escrow',
    businessSubtype: 'domain_digital_assets',
    paymentOptions: [],
    deliveryOptions: ['xproxy'],
    steps: [
      walletGateStep('Define swap'),
      {
        id: 'amount',
        title: 'Amount & asset',
        description: 'How much USDC or crypto are you swapping, and what fiat do you need?',
        fields: [
          { id: 'cryptoAmount', label: 'Crypto amount (USDC)', type: 'number', required: true },
          { id: 'fiatTarget', label: 'Cash needed (JMD)', type: 'number', required: true },
          { id: 'meetLocation', label: 'Meetup area', type: 'text', placeholder: 'Kingston, Half Way Tree…' },
        ],
      },
      {
        id: 'counterparty',
        title: 'Find counterparty',
        description: 'Post to Town Hall or match with a Vanguard Runner from the noticeboard.',
      },
      {
        id: 'escrow',
        title: 'Lock escrow & release on PIN',
        description: 'Funds stay non-custodial until Handshake PIN confirms cash received.',
      },
    ],
    launchLabel: 'Start xChange flow',
  },
  {
    id: 'xbuy',
    name: 'xBuy',
    shortName: 'xBuy',
    tagline: 'Pay with digital assets for goods or services',
    description: 'Buy physical goods or hire services — pay from wallet escrow when the deal closes.',
    icon: 'ShoppingBag',
    accent: '#f59e0b',
    launchHref: '/marketplace?intent=xbuy',
    hubPath: '/apps/xbuy',
    businessType: 'trustless_services',
    businessSubtype: 'freelance_digital',
    paymentOptions: ['xchange', 'xproxy'],
    deliveryOptions: ['xcourier'],
    steps: [
      walletGateStep('Describe purchase'),
      {
        id: 'item',
        title: 'What are you buying?',
        description: 'Describe the good or service and preferred seller if you have one.',
        fields: [
          { id: 'itemTitle', label: 'Item or service', type: 'text', required: true },
          { id: 'budget', label: 'Budget (USDC)', type: 'number' },
          { id: 'notes', label: 'Details', type: 'textarea' },
        ],
      },
      {
        id: 'payment',
        title: 'Choose payment path',
        description: 'Direct escrow, xChange cash swap, or xProxy middle-person purchase.',
      },
      {
        id: 'delivery',
        title: 'Delivery (optional)',
        description: 'Add xCourier runner pickup/delivery or meet in person.',
      },
      {
        id: 'fund',
        title: 'Fund & track',
        description: 'Complete checkout and monitor progress in Activity.',
      },
    ],
    launchLabel: 'Start xBuy flow',
  },
  {
    id: 'xstays',
    name: 'xStays',
    shortName: 'xStays',
    tagline: 'Rent anything with digital assets',
    description: 'Short-term rentals, peer car sharing, equipment — deposit via UTT, check-in PIN at handoff.',
    icon: 'Home',
    accent: '#ec4899',
    launchHref: '/marketplace?intent=xstays',
    hubPath: '/apps/xstays',
    businessType: 'rentals',
    businessSubtype: 'short_term_rental',
    paymentOptions: ['xchange', 'xproxy'],
    deliveryOptions: [],
    steps: [
      walletGateStep('Pick dates'),
      {
        id: 'dates',
        title: 'Dates & asset',
        description: 'When do you need the rental and what type of asset?',
        fields: [
          { id: 'checkIn', label: 'Start date', type: 'date', required: true },
          { id: 'checkOut', label: 'End date', type: 'date', required: true },
          {
            id: 'assetType',
            label: 'Rental type',
            type: 'select',
            required: true,
            options: [
              { value: 'short_term', label: 'Short-term stay' },
              { value: 'vehicle', label: 'Vehicle' },
              { value: 'equipment', label: 'Equipment' },
              { value: 'fashion', label: 'Fashion' },
            ],
          },
        ],
      },
      {
        id: 'listing',
        title: 'Select listing',
        description: 'Browse rental storefronts or post a request to Town Hall.',
      },
      {
        id: 'deposit',
        title: 'Deposit & check-in PIN',
        description: 'Escrow holds deposit until check-out; PIN confirms handoff at pickup.',
      },
    ],
    launchLabel: 'Start xStays flow',
  },
  {
    id: 'xcourier',
    name: 'Services',
    shortName: 'Services',
    tagline: 'Service providers',
    description:
      'Find a trade or join as a provider — shared category list for search and becoming a provider.',
    icon: 'Bike',
    accent: '#06b6d4',
    launchHref: '/runner?intent=xcourier',
    hubPath: '/apps/xcourier',
    businessType: 'delegated_supply_chain',
    businessSubtype: 'restaurant_delivery',
    paymentOptions: ['xchange'],
    deliveryOptions: [],
    steps: [
      walletGateStep('Route details'),
      {
        id: 'route',
        title: 'Pickup & dropoff',
        description: 'Where should the runner collect and deliver?',
        fields: [
          { id: 'pickup', label: 'Pickup address', type: 'text', required: true },
          { id: 'dropoff', label: 'Dropoff address', type: 'text', required: true },
          { id: 'instructions', label: 'Instructions', type: 'textarea' },
        ],
      },
      {
        id: 'fund',
        title: 'Fund courier escrow',
        description: 'Set USDC amount — released to runner on delivery PIN.',
        fields: [{ id: 'escrowAmount', label: 'Escrow (USDC)', type: 'number', required: true }],
      },
      {
        id: 'claim',
        title: 'Runner claims & delivers',
        description: 'Track live on noticeboard until PIN settlement.',
      },
    ],
    launchLabel: 'Open Services',
  },
  {
    id: 'xproxy',
    name: 'Payers & Delivery',
    shortName: 'Pay & Deliver',
    tagline: 'Payers, Prepping & Delivery',
    description:
      'Request someone to pay for something, prep, package, or deliver.',
    icon: 'UserRoundCheck',
    accent: '#8b5cf6',
    launchHref: '/runner?intent=xproxy',
    hubPath: '/apps/xproxy',
    businessType: 'delegated_supply_chain',
    businessSubtype: 'generic_delegated_supply_chain',
    paymentOptions: ['xchange'],
    deliveryOptions: ['xcourier'],
    steps: [
      walletGateStep('Proxy request'),
      {
        id: 'request',
        title: 'What should the proxy buy or rent?',
        description: 'Describe the cash purchase or rental the runner will execute on your behalf.',
        fields: [
          { id: 'purchaseDesc', label: 'Purchase / rental description', type: 'textarea', required: true },
          { id: 'cashBudget', label: 'Cash budget (JMD)', type: 'number', required: true },
          { id: 'cryptoLock', label: 'Crypto to lock (USDC)', type: 'number', required: true },
        ],
      },
      {
        id: 'premium',
        title: 'Runner premium',
        description: 'Delegate tier sets ~15–20% premium — runner earns crypto on successful PIN handoff.',
      },
      {
        id: 'pin',
        title: 'Handshake PIN release',
        description: 'Confirm receipt in person or via secure channel to release escrow.',
      },
    ],
    launchLabel: 'Request a Runner',
  },
  {
    id: 'xevents',
    name: 'xEvents',
    shortName: 'Events',
    tagline: 'Meetups, tickets, and venue PINs',
    description: 'Organize paid or free events — attendees acquire PINs for venue access and participation.',
    icon: 'Ticket',
    accent: '#ef4444',
    launchHref: '/merchant?intent=xevents',
    hubPath: '/apps/xevents',
    businessType: 'access_saas',
    businessSubtype: 'event_ticketing',
    paymentOptions: ['xchange', 'xbuy'],
    deliveryOptions: [],
    steps: [
      {
        id: 'organizer',
        title: 'Organizer setup',
        description: 'Merchant persona required to publish ticketed events with PIN access control.',
        gate: 'persona_merchant',
      },
      {
        id: 'event',
        title: 'Event details',
        description: 'Title, venue, date, and free vs paid admission.',
        fields: [
          { id: 'eventTitle', label: 'Event name', type: 'text', required: true },
          { id: 'venue', label: 'Venue', type: 'text', required: true },
          { id: 'eventDate', label: 'Date', type: 'date', required: true },
          {
            id: 'admission',
            label: 'Admission',
            type: 'select',
            options: [
              { value: 'free', label: 'Free' },
              { value: 'paid', label: 'Paid (USDC)' },
            ],
          },
        ],
      },
      {
        id: 'pins',
        title: 'Issue access PINs',
        description: 'Attendees receive PINs after payment — scan or enter at venue.',
      },
      {
        id: 'attend',
        title: 'Participate',
        description: 'Buy a ticket or RSVP — your PIN appears in Activity until the event ends.',
      },
    ],
    launchLabel: 'Plan xEvent',
  },
];

export const BX_APP_MAP: Record<BxAppId, BxAppDefinition> = BX_APPS.reduce(
  (acc, app) => {
    acc[app.id] = app;
    return acc;
  },
  {} as Record<BxAppId, BxAppDefinition>
);

export const getBxApp = (id: string): BxAppDefinition | null =>
  BX_APP_MAP[id as BxAppId] ?? null;

export const getVisibleBxApps = (options: {
  showMerchantNav?: boolean;
  showRunnerNav?: boolean;
}): BxAppDefinition[] => {
  const { showMerchantNav = true, showRunnerNav = true } = options;
  return BX_APPS.filter(app => {
    if (app.requiresPersona === 'merchant' && !showMerchantNav) {
      return false;
    }
    if (app.requiresPersona === 'runner' && !showRunnerNav) {
      return false;
    }
    return true;
  });
};

export const getAppLabel = (id: BxAppId): string => BX_APP_MAP[id]?.shortName ?? id;
